/**
 * NfceStatusSyncService
 *
 * Reconcilia o status local das NFC-e com o back/web.
 *
 * Motivação: quando o back/web cancela uma NFC-e, o terminal continua exibindo
 * "Autorizada" porque nunca é avisado da mudança. Este serviço consulta o back,
 * recebe as NFC-e com status atualizado e atualiza o banco local (status,
 * statusTransmissao, status_fiscal, protocolo/data/XML de cancelamento, etc.).
 *
 * É seguro chamar em diferentes momentos (abertura do PDV, atualização da lista
 * de vendas, sync online): nunca lança para o chamador — apenas registra e
 * devolve um resumo. Só roda em modo desktop e com internet disponível.
 */
import { getElectronAPI, isDesktopMode } from "@/plugins/desktop";
import ApiOfflineService from "./ApiOfflineService";
import NfceLocalRepository from "@/modules/PdvDesktop/repositories/NfceLocalRepository";
import TerminalConfigService from "./TerminalConfigService";

/**
 * Status locais que ainda podem virar "cancelada" no back e que, portanto,
 * vale a pena consultar. Notas já canceladas localmente são ignoradas.
 */
const STATUS_CONSULTAVEIS = ["autorizada", "pendente_transmissao_backend", "pendente_transmissao"];

function _temInternet() {
  try {
    return typeof navigator === "undefined" || navigator.onLine !== false;
  } catch (_) {
    return true;
  }
}

async function _obterContextoTerminal(api) {
  let info = {};
  try { info = (await api.terminal.getInfo()) || {}; } catch (_) { info = {}; }

  let device_id = "";
  try { device_id = (await api.device.getDeviceId()) || ""; } catch (_) { device_id = ""; }

  return {
    uuid_terminal:     info.uuid_terminal || "",
    device_id,
    id_terminal_venda: info.id_terminal_venda != null ? Number(info.id_terminal_venda) : null,
    id_empresa:        info.id_empresa        != null ? Number(info.id_empresa)        : null,
  };
}

/**
 * Reúne os identificadores das NFC-e locais que ainda podem ter sido canceladas
 * no back (status consultável). Limita o volume para não pesar na chamada.
 */
async function _coletarCandidatas(api) {
  const candidatas = [];
  for (const status of STATUS_CONSULTAVEIS) {
    let lista = [];
    try { lista = (await api.nfce.listarPorStatusFiscal(status)) || []; } catch (_) { lista = []; }
    for (const n of lista) {
      candidatas.push({
        uuid_venda_terminal: n.uuid_venda_terminal,
        chave:               n.chave        ?? null,
        numero:              n.numero       ?? n.numeroNota ?? null,
        serie:               n.serie        ?? null,
        modelo:              n.modelo       ?? null,
        id_nfce_web:         n.id_nfce_web  ?? null,
      });
    }
  }
  return candidatas;
}

const NfceStatusSyncService = {
  /**
   * Consulta o back e aplica localmente os status atualizados das NFC-e.
   *
   * @returns {Promise<{ ok: boolean, atualizadas: number, canceladas: number, motivo?: string }>}
   */
  async sincronizarStatusNfces() {
    const vazio = { ok: true, atualizadas: 0, canceladas: 0 };

    if (!isDesktopMode()) return { ...vazio, motivo: "nao_desktop" };
    if (!_temInternet())  return { ...vazio, motivo: "offline" };
    if (TerminalConfigService.isOnlineBloqueado()) return { ...vazio, motivo: "terminal_bloqueado" };

    const api = getElectronAPI();
    if (!api) return { ...vazio, motivo: "sem_api" };

    try {
      const ctx = await _obterContextoTerminal(api);
      if (!ctx.uuid_terminal) return { ...vazio, motivo: "terminal_nao_configurado" };

      const candidatas = await _coletarCandidatas(api);
      // Sem nenhuma nota consultável: nada a fazer.
      if (candidatas.length === 0) return { ...vazio, motivo: "sem_candidatas" };

      const resposta = await ApiOfflineService.consultarStatusNfces({
        uuid_terminal:     ctx.uuid_terminal,
        device_id:         ctx.device_id,
        id_terminal_venda: ctx.id_terminal_venda,
        id_empresa:        ctx.id_empresa,
        nfces:             candidatas,
      });

      const remotos =
        (Array.isArray(resposta) && resposta) ||
        resposta?.nfces ||
        resposta?.resultados ||
        resposta?.data ||
        [];

      let canceladas = 0;
      for (const remoto of remotos) {
        try {
          const r = await NfceLocalRepository.aplicarStatusRemoto(remoto);
          if (r && r.atualizado) canceladas++;
        } catch (e) {
          console.warn("[NfceStatusSyncService] Falha ao aplicar status remoto:", e?.message ?? e);
        }
      }

      return { ok: true, atualizadas: canceladas, canceladas };
    } catch (e) {
      console.warn("[NfceStatusSyncService] sincronizarStatusNfces:", e?.message ?? e);
      return { ...vazio, motivo: "erro", erro: e?.message ?? String(e) };
    }
  },
};

export default NfceStatusSyncService;
